import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  activities_selected: 'Activities Selected',
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  needs_revision: 'Needs Revision',
};

const STATUS_COLORS = {
  not_started: { bg: 'var(--bg)', color: 'var(--text-3)', border: 'var(--border)' },
  in_progress: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  activities_selected: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  submitted: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  under_review: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  approved: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  needs_revision: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
};

function daysRemaining(deadline) {
  const diff = new Date(deadline) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function daysSinceStart(startDate) {
  const diff = new Date() - new Date(startDate);
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function getReminders(sprint) {
  if (!sprint || sprint.overallStatus === 'completed') return [];
  const days = daysSinceStart(sprint.startDate);
  const reminders = [];

  // Task 1 reminders
  if (sprint.task1.status === 'not_started' || sprint.task1.status === 'in_progress') {
    if (days >= 50) reminders.push({ type: 'warning', text: 'Task 1 closes in ' + daysRemaining(sprint.task1Deadline) + ' days. Upload your one page now.' });
    else if (days >= 30) reminders.push({ type: 'info', text: 'One month in. Task 1 deadline is in ' + daysRemaining(sprint.task1Deadline) + ' days.' });
    else if (days >= 7 && sprint.task1.status === 'not_started') reminders.push({ type: 'info', text: "You haven't started Task 1 yet. It takes one afternoon. Start today." });
  }

  // Task 2 reminders
  if (['not_started', 'activities_selected', 'in_progress'].includes(sprint.task2.status)) {
    if (days >= 80) reminders.push({ type: 'warning', text: 'Task 2 closes in ' + daysRemaining(sprint.task2Deadline) + ' days. Submit now.' });
    else if (days >= 60) reminders.push({ type: 'info', text: 'Task 2 closes in ' + daysRemaining(sprint.task2Deadline) + ' days. Complete your four activities.' });
    else if (days >= 30 && sprint.task2.status === 'not_started') reminders.push({ type: 'info', text: 'Have you selected your 4 activities for Task 2?' });
  }

  return reminders;
}

export default function Sprint() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sprint, setSprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/sprint/status');
        setSprint(data.sprint);
      } catch { /* no sprint */ }
      setLoading(false);
    })();
  }, []);

  const handleBegin = async () => {
    setStarting(true);
    setError('');
    try {
      const { data } = await api.post('/sprint/begin');
      setSprint(data.sprint);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start Sprint.');
    }
    setStarting(false);
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
          <p style={{ color: 'var(--text-3)', fontSize: '14px' }}>Loading Sprint...</p>
        </div>
      </div>
    );
  }

  // No active sprint — show intro + begin button
  if (!sprint) {
    return (
      <div style={s.page}>
        <div style={s.hero}>
          <p style={s.tagline}>SPRINT</p>
          <h1 style={s.title}>Conversations with Nitin Potdar</h1>
          <p style={s.subtitle}>A structured introduction to the serious habits and knowledge that a legal career actually requires.</p>
        </div>

        <div style={s.introCard}>
          <p style={{ ...s.introText, fontFamily: "'Playfair Display', Georgia, serif", fontWeight: '700', color: 'var(--navy)' }}>
            {user?.name ? `Dear ${user.name.split(' ')[0]}` : 'Dear Student'}
          </p>
          <p style={s.introText}>Sprint is not a course. It is not a certification programme. It is a structured introduction to the serious habits and knowledge that a legal career actually requires — given to you at the beginning of your journey.</p>
          <p style={s.introText}>Complete two tasks. Earn a one-on-one Zoom session with Nitin Potdar. Thirty minutes that could change the direction of your career.</p>
          <p style={{ ...s.introText, fontStyle: 'italic', color: 'var(--text-2)' }}>– Nitin Potdar</p>
        </div>

        <div style={s.howItWorks}>
          <p className="section-cap" style={{ marginBottom: '14px' }}>How It Works</p>
          <div style={s.stepList}>
            {[
              'Tap "Begin Sprint" below. Your two timelines start immediately.',
              'Task 1 — Know Your Law School. Submit one A4 page within 2 months.',
              'Task 2 — Building Your Professional Foundation. Choose 4 of 10 activities, submit responses within 3 months.',
              'Once both tasks are approved, write to Nitin on LinkedIn for your Zoom session.',
            ].map((text, i) => (
              <div key={i} style={s.step}>
                <span style={s.stepNum}>{i + 1}</span>
                <p style={s.stepText}>{text}</p>
              </div>
            ))}
          </div>
        </div>

        {error && <p style={s.error}>{error}</p>}

        <button
          onClick={handleBegin}
          disabled={starting}
          className="primary-btn"
          style={s.beginBtn}
        >
          {starting ? 'Starting...' : 'Begin Sprint'}
        </button>
      </div>
    );
  }

  // Active or completed sprint — show dashboard
  const reminders = getReminders(sprint);
  const t1Status = sprint.task1?.status || 'not_started';
  const t2Status = sprint.task2?.status || 'not_started';
  const t1Colors = STATUS_COLORS[t1Status];
  const t2Colors = STATUS_COLORS[t2Status];

  return (
    <div style={s.page}>
      <div style={s.hero}>
        <p style={s.tagline}>SPRINT</p>
        <h1 style={s.title}>Your Sprint Dashboard</h1>
        <p style={s.subtitle}>
          Started {new Date(sprint.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Completed banner */}
      {sprint.overallStatus === 'completed' && (
        <div style={{ ...s.introCard, background: '#d1fae5', borderColor: '#6ee7b7' }}>
          <p style={{ ...s.introText, fontWeight: '700', color: '#065f46' }}>Both tasks approved!</p>
          <p style={s.introText}>Write to Nitin on LinkedIn:</p>
          <div style={s.linkedinMsg}>
            <p style={{ ...s.introText, fontStyle: 'italic', fontSize: '13px' }}>
              "Hi Nitin. I am {user?.name || '[Name]'}, a {user?.year || '[Year]'} year student at {user?.college || '[College]'}.
              I have completed Sprint Task 1 and Task 2 on Ask Nitin.
              I would be grateful for our Zoom session."
            </p>
          </div>
        </div>
      )}

      {/* Reminders */}
      {reminders.map((r, i) => (
        <div key={i} style={{ ...s.reminder, ...(r.type === 'warning' ? s.reminderWarning : s.reminderInfo) }}>
          <p style={{ margin: 0, fontSize: '13px' }}>{r.text}</p>
        </div>
      ))}

      {/* Task Cards */}
      <p className="section-cap" style={{ marginBottom: '14px' }}>Tasks</p>
      <div className="dash-grid" style={{ marginBottom: '28px' }}>
        {/* Task 1 */}
        <Link to="/sprint/task1" style={{ textDecoration: 'none' }}>
          <div style={s.card} className="feature-card">
            <div style={s.cardTop}>
              <div style={s.cardIconWrap}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
              </div>
              <span style={{ ...s.cardTag, background: t1Colors.bg, color: t1Colors.color, borderColor: t1Colors.border }}>
                {STATUS_LABELS[t1Status]}
              </span>
            </div>
            <h2 style={s.cardTitle}>Task 1: Know Your Law School</h2>
            <p style={s.cardDesc}>Explore six areas of your law school and submit one A4 page.</p>
            <div style={s.cardMeta}>
              <span style={s.deadline}>Deadline: {new Date(sprint.task1Deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              <span style={s.daysLeft}>{daysRemaining(sprint.task1Deadline)} days left</span>
            </div>
            <div style={s.cardCta}>
              <span>{t1Status === 'not_started' ? 'Start' : 'View'}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
          </div>
        </Link>

        {/* Task 2 */}
        <Link to="/sprint/task2" style={{ textDecoration: 'none' }}>
          <div style={s.card} className="feature-card">
            <div style={s.cardTop}>
              <div style={s.cardIconWrap}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </div>
              <span style={{ ...s.cardTag, background: t2Colors.bg, color: t2Colors.color, borderColor: t2Colors.border }}>
                {STATUS_LABELS[t2Status]}
              </span>
            </div>
            <h2 style={s.cardTitle}>Task 2: Building Your Professional Foundation</h2>
            <p style={s.cardDesc}>Choose 4 of 10 activities and submit your responses.</p>
            <div style={s.cardMeta}>
              <span style={s.deadline}>Deadline: {new Date(sprint.task2Deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              <span style={s.daysLeft}>{daysRemaining(sprint.task2Deadline)} days left</span>
            </div>
            <div style={s.cardCta}>
              <span>{t2Status === 'not_started' ? 'Start' : 'View'}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
          </div>
        </Link>
      </div>

      {/* Nitin quote */}
      <div style={s.quoteCard}>
        <p style={{ ...s.introText, fontStyle: 'italic', color: 'var(--text-2)', fontSize: '13px' }}>
          "The students who know the map from year one are the ones who are genuinely prepared when the opportunities begin to arrive."
        </p>
        <p style={{ ...s.introText, fontWeight: '600', color: 'var(--text-3)', fontSize: '12px', marginTop: '6px' }}>— Nitin Potdar</p>
      </div>
    </div>
  );
}

const s = {
  page: { maxWidth: '960px', margin: '0 auto', padding: '32px 20px 40px' },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '80px 0' },
  spinner: { width: '28px', height: '28px', border: '3px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  hero: { marginBottom: '28px', paddingBottom: '20px', borderBottom: '1px solid var(--border-2)' },
  tagline: { fontSize: '11px', fontWeight: '700', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '8px' },
  title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '32px', fontWeight: '700', color: 'var(--navy)', marginBottom: '6px', lineHeight: '1.2' },
  subtitle: { fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.7' },
  introCard: { background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '10px' },
  introText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.7', margin: 0 },
  linkedinMsg: { background: '#fff', borderRadius: '8px', padding: '12px 16px', border: '1px solid var(--border)' },
  howItWorks: { marginBottom: '28px' },
  stepList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  step: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  stepNum: { width: '28px', height: '28px', borderRadius: '50%', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  stepText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.6', margin: 0 },
  beginBtn: { width: '100%', padding: '14px', fontSize: '15px', fontWeight: '700', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer' },
  error: { color: 'var(--danger)', fontSize: '13px', marginBottom: '12px' },
  reminder: { borderRadius: '10px', padding: '12px 16px', marginBottom: '12px', border: '1px solid' },
  reminderInfo: { background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' },
  reminderWarning: { background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' },
  card: { background: '#fff', borderRadius: '14px', padding: '22px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: '10px' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardIconWrap: { width: '42px', height: '42px', borderRadius: '10px', background: 'var(--gold-light)', color: 'var(--navy-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTag: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', padding: '3px 9px', borderRadius: '99px', border: '1px solid' },
  cardTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17px', fontWeight: '700', color: 'var(--navy)' },
  cardDesc: { fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.6' },
  cardMeta: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-3)' },
  deadline: {},
  daysLeft: { fontWeight: '600' },
  cardCta: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700', color: 'var(--gold)', marginTop: '4px' },
  quoteCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 24px' },
};
